/**
 * @file Region Utils
 * @description Utility functions for region component
 */

import { ANIMATION } from "./constants.js";

/**
 * Find corresponding navigation link for SVG region
 * @param {string} regionSlug - Region slug from SVG id
 * @param {string} selector - Selector for region items
 * @returns {HTMLElement|null} Corresponding navigation link
 */
export function findCorrespondingNavLink(regionSlug, selector) {
  if (!regionSlug) return null;

  // Find navigation link with matching data-region attribute
  const navLink = document.querySelector(`${selector}[data-region="${regionSlug}"]`);
  return navLink;
}

/**
 * Get term ID from navigation link
 * @param {string} regionSlug - Region slug
 * @param {string} selector - Selector for region items
 * @returns {string|null} Term ID or null if not found
 */
export function getTermIDFromSlug(regionSlug, selector) {
  const navLink = findCorrespondingNavLink(regionSlug, selector);
  return navLink?.getAttribute("data-id") || null;
}

/**
 * Toggle loading indicator
 * @param {HTMLElement} loadingIndicator - Loading indicator element
 * @param {boolean} isLoading - Whether to show or hide loading
 */
export function toggleLoading(loadingIndicator, isLoading) {
  if (loadingIndicator) {
    loadingIndicator.style.display = isLoading ? "flex" : "none";
  }
}

/**
 * Hide element with proper state management
 * @param {HTMLElement} element - Element to hide
 * @returns {Promise} Promise that resolves when element is hidden
 */
export function hideElement(element) {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }

    // Use smooth animation like original code
    element.style.transition = `opacity ${ANIMATION.TRANSITION_DURATION} ease-in-out`;
    element.style.opacity = "0";

    setTimeout(() => {
      element.style.visibility = "hidden";
      element.style.zIndex = "-1";
      element.style.display = "none";
      resolve();
    }, ANIMATION.HIDE_DELAY);
  });
}

/**
 * Show element with proper state management
 * @param {HTMLElement} element - Element to show
 * @returns {Promise} Promise that resolves when element is shown
 */
export function showElement(element) {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }

    // Use smooth animation like original code
    element.style.display = "block";
    element.style.visibility = "visible";
    element.style.zIndex = "100";
    element.style.transition = `opacity ${ANIMATION.TRANSITION_DURATION} ease-in-out`;

    // Trigger reflow before setting opacity
    element.offsetHeight;
    element.style.opacity = "1";

    setTimeout(resolve, ANIMATION.SHOW_DELAY);
  });
}

/**
 * Fetch data from API with error handling
 * @param {string} url - API URL
 * @returns {Promise<Object>} API response data
 */
export async function fetchData(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
}

/**
 * Find child map by region slug
 * @param {string} regionSlug - Region slug
 * @param {string} selector - Selector for child maps
 * @returns {HTMLElement|null} Child map element
 */
export function findChildMap(regionSlug, selector) {
  if (!regionSlug) return null;

  // Method 1: Direct querySelector
  let childMap = document.querySelector(`${selector}[data-map="${regionSlug}"]`);

  // Method 2: Case-insensitive search
  if (!childMap) {
    const allMaps = Array.from(document.querySelectorAll(selector));
    childMap = allMaps.find(
      (map) => map.getAttribute("data-map")?.toLowerCase() === regionSlug.toLowerCase(),
    );
  }

  // Method 3: Partial match search
  if (!childMap) {
    const allMaps = Array.from(document.querySelectorAll(selector));
    childMap = allMaps.find(
      (map) =>
        map.getAttribute("data-map")?.includes(regionSlug) ||
        regionSlug.includes(map.getAttribute("data-map")),
    );
  }

  return childMap;
}

/**
 * Generate tooltip content for region
 * @param {Object} data - Region data
 * @returns {string} HTML content for tooltip
 */
export function generateTooltipContent(data) {
  if (!data) return "No data available";

  const {
    name: title,
    industry_post_count: count,
    acf: {
      location_population: population,
      location_area: totalArea,
      location_salary: salary,
      location_province_num: provinceNum,
      location_in_airport: inAirport,
      location_do_airport: doAirport,
      location_seaport: seaport,
    } = {},
  } = data;

  let contentHTML = `<h4 class="font-semibold">${title}</h4><table class="table">`;

  if (count)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Số KCN: </td><td class="p-1">${count}</td></tr>`;
  if (population)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Dân số: </td><td class="p-1">${population}</td></tr>`;
  if (salary)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Lương tối thiểu: </td><td class="p-1">${salary}</td></tr>`;
  if (totalArea)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Diện tích: </td><td class="p-1">${totalArea}</td></tr>`;
  if (provinceNum)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Số tỉnh: </td><td class="p-1">${provinceNum}</td></tr>`;
  if (inAirport)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Sân bay nội địa: </td><td class="p-1">${inAirport}</td></tr>`;
  if (doAirport)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Sân bay quốc tế: </td><td class="p-1">${doAirport}</td></tr>`;
  if (seaport)
    contentHTML += `<tr><td class="p-1 pl-0 whitespace-nowrap">Cảng biển: </td><td class="p-1">${seaport}</td></tr>`;

  contentHTML += "</table>";
  return contentHTML;
}
